// Ziyaretçi analizi izleyicisi.
//
// Sitenin herkese açık sayfalarında (ve üye panelinde) şunları toplar ve
// POST /api/analytics/collect ucuna toplu gönderir:
//   pageview  her sayfa görüntüleme (oturumun ilk sayfası "giriş sayfası")
//   engage    sayfada geçen ETKİN süre (sekme görünürken) ve en derin kaydırma
//   click     tıklama konumu (ısı haritası) ve tıklanan öğe / bağlantı
//   search    site içi arama kutularına yazılan sözcükler
//   vital     sayfa hızı ölçümleri (LCP, FCP, CLS, FID, TTFB)
//   error     yakalanmamış tarayıcı hataları
//
// Kimlikler tarayıcıda üretilen rastgele değerlerdir (çerez değil,
// localStorage); kişisel bilgi toplanmaz. Yönetim paneli, yayın kaplamaları,
// ısı haritası önizlemesi (iframe) ve "bu tarayıcıyı sayma" seçilmiş
// tarayıcılar izlenmez.
//
// Sayfa kapanırken kalan olaylar navigator.sendBeacon ile gönderilir; beacon
// CORS ön isteği yapamadığı için gövde text/plain'dir.

import api from "../api/api";
import { isOverlayPath } from "../appShell";

const VISITOR_KEY = "el_vid";
const SESSION_KEY = "el_sess";
export const OPT_OUT_KEY = "el_no_track";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_MS = 5000;
const MAX_QUEUE = 20;
const MAX_CLICKS_PER_VIEW = 60;
const MAX_ERRORS_PER_VIEW = 5;
const SEARCH_SETTLE_MS = 1500;
// Son kullanıcı hareketinden (kaydırma, tıklama, tuş, fare) bu kadar sonra
// sekme açık dursa da süre sayılmaz: unutulan sekme "etkin süre" şişirmesin.
const IDLE_MS = 60 * 1000;

/* ------------------------- yardımcılar ------------------------- */

const storage = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch { /* gizli sekme */ }
  },
};

export const randomId = (length = 20) => {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
};

/** İzlenmeyen yollar: yönetim kabuğu ve yayın kaplamaları. */
export const isTrackablePath = (pathname = "") =>
  !/^\/(editor|yonetim|admin)(\/|$)/.test(pathname) && !isOverlayPath(pathname);

export const isOptedOut = () => storage.get(OPT_OUT_KEY) === "1";
export const setOptOut = (value) => storage.set(OPT_OUT_KEY, value ? "1" : "0");

const collectUrl = () => {
  const base = String(api.defaults?.baseURL || "").replace(/\/+$/, "");
  return `${base}/api/analytics/collect`;
};

/** Tıklanan öğenin kısa tarifi: "a.nav-link Puan durumu". */
export const describeElement = (el) => {
  if (!el || el.nodeType !== 1) return "";
  const target = el.closest?.("a,button,[role=button],[role=tab],input,select,textarea,label,summary,[onclick]") || el;
  const tag = target.tagName.toLowerCase();
  const id = target.id ? `#${target.id}` : "";
  const cls = typeof target.className === "string" && target.className.trim() ? `.${target.className.trim().split(/\s+/)[0]}` : "";
  const text = (target.getAttribute("aria-label") || target.innerText || target.textContent || target.value || target.getAttribute("title") || "")
    .replace(/\s+/g, " ").trim().slice(0, 60);
  return `${tag}${id}${cls}${text ? ` ${text}` : ""}`.slice(0, 200);
};

const linkOf = (el) => {
  const a = el?.closest?.("a[href]");
  if (!a) return null;
  const href = a.getAttribute("href") || "";
  if (/^(tel|mailto):/i.test(href)) return href.slice(0, 400);
  try {
    const url = new URL(href, window.location.href);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}`.slice(0, 400) : url.href.slice(0, 400);
  } catch {
    return href.slice(0, 400);
  }
};

const SEARCH_INPUT = 'input[type="search"], [role="searchbox"], input[data-analytics-search], input[name*="search" i], input[name*="arama" i]';

/* ------------------------- izleyici ------------------------- */

export function createTracker({ send, now = () => Date.now(), win = window, doc = document } = {}) {
  const state = {
    started: false,
    session: null,
    visitorId: null,
    queue: [],
    view: null, // { id, path, visibleSince, activeMs, sentMs, maxScroll, sentScroll, clicks, errors }
    firstLoad: true,
    lastActivity: 0,
    searchTimers: new Map(),
    lastSearch: new Map(),
    timer: null,
  };

  const transport = send || ((payload, { beacon } = {}) => {
    const body = JSON.stringify(payload);
    const url = collectUrl();
    if (beacon && win.navigator?.sendBeacon) {
      try {
        if (win.navigator.sendBeacon(url, new Blob([body], { type: "text/plain;charset=UTF-8" }))) return;
      } catch { /* fetch ile dene */ }
    }
    try {
      win.fetch?.(url, { method: "POST", body, credentials: "include", keepalive: true, headers: { "Content-Type": "text/plain;charset=UTF-8" } }).catch(() => {});
    } catch { /* yut */ }
  });

  const ensureVisitor = () => {
    let id = storage.get(VISITOR_KEY);
    let isNew = false;
    if (!id || !/^[a-z0-9]{8,40}$/.test(id)) {
      id = randomId();
      storage.set(VISITOR_KEY, id);
      isNew = true;
    }
    state.visitorId = id;
    return isNew;
  };

  const readUtm = () => {
    try {
      const params = new URLSearchParams(win.location.search);
      const utm = { source: params.get("utm_source"), medium: params.get("utm_medium"), campaign: params.get("utm_campaign") };
      return utm.source ? utm : null;
    } catch {
      return null;
    }
  };

  /** Oturum: 30 dk hareketsizlikte ya da yeni bir kampanya bağlantısıyla yenilenir. */
  const touchSession = () => {
    const t = now();
    let session = null;
    try { session = JSON.parse(storage.get(SESSION_KEY) || "null"); } catch { session = null; }
    const utm = readUtm();
    const expired = !session || !session.id || t - Number(session.last || 0) > SESSION_IDLE_MS;
    const newCampaign = utm && session?.utm?.source !== utm.source;
    let started = false;
    if (expired || newCampaign) {
      const visitorIsNew = ensureVisitor();
      session = {
        id: randomId(),
        last: t,
        ref: state.firstLoad ? String(doc.referrer || "").slice(0, 500) : "",
        utm: utm || null,
        nv: visitorIsNew ? 1 : 0,
        pages: 0,
      };
      started = true;
    } else {
      ensureVisitor();
      session.last = t;
    }
    if (state.session && state.session.id !== session.id) flush();
    state.session = session;
    storage.set(SESSION_KEY, JSON.stringify(session));
    return started;
  };

  const context = () => ({
    ref: state.session?.ref || "",
    utm: state.session?.utm || {},
    sw: win.screen?.width,
    sh: win.screen?.height,
    vw: win.innerWidth,
    lang: win.navigator?.language,
    tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; } })(),
    touch: (win.navigator?.maxTouchPoints || 0) > 1,
  });

  function flush({ beacon = false } = {}) {
    if (!state.queue.length || !state.session) return;
    const payload = { v: state.visitorId, s: state.session.id, nv: state.session.nv, ctx: context(), e: state.queue.splice(0, 60) };
    transport(payload, { beacon });
    if (state.queue.length) flush({ beacon });
  }

  const push = (event) => {
    state.queue.push(event);
    if (state.queue.length >= MAX_QUEUE) flush();
  };

  /* --- etkin süre ve kaydırma --- */

  const scrollPct = () => {
    const el = doc.documentElement;
    const height = Math.max(el?.scrollHeight || 0, doc.body?.scrollHeight || 0);
    if (!height) return 0;
    const bottom = (win.scrollY || 0) + (win.innerHeight || 0);
    return Math.max(0, Math.min(100, Math.round((bottom / height) * 100)));
  };

  const markActive = () => { state.lastActivity = now(); };

  const accrue = () => {
    const view = state.view;
    if (!view || view.visibleSince == null) return;
    const t = now();
    const end = Math.min(t, state.lastActivity + IDLE_MS);
    if (end > view.visibleSince) view.activeMs += end - view.visibleSince;
    view.visibleSince = t;
  };

  const sendEngage = () => {
    const view = state.view;
    if (!view) return;
    accrue();
    const delta = Math.round(view.activeMs - view.sentMs);
    if (delta < 500 && view.maxScroll <= view.sentScroll) return;
    push({ t: "engage", pv: view.id, p: view.path, d: Math.max(0, delta), sc: view.maxScroll });
    view.sentMs = view.activeMs;
    view.sentScroll = view.maxScroll;
  };

  /* --- olay dinleyicileri --- */

  const onScroll = () => {
    if (!state.view) return;
    accrue();
    markActive();
    state.view.maxScroll = Math.max(state.view.maxScroll, scrollPct());
  };

  let lastMove = 0;
  const onActivity = () => {
    const t = now();
    if (t - lastMove < 5000) return;
    lastMove = t;
    accrue();
    markActive();
  };

  const onVisibility = () => {
    if (!state.view) return;
    if (doc.visibilityState === "hidden") {
      sendEngage();
      state.view.visibleSince = null;
      flush({ beacon: true });
    } else {
      state.view.visibleSince = now();
      markActive();
      touchSession();
    }
  };

  const onClick = (event) => {
    const view = state.view;
    if (!view) return;
    accrue();
    markActive();
    if (view.clicks >= MAX_CLICKS_PER_VIEW) return;
    const width = win.innerWidth || 1;
    view.clicks += 1;
    push({
      t: "click",
      pv: view.id,
      p: view.path,
      x: Math.round((Math.max(0, event.clientX) / width) * 1000),
      y: Math.round((event.pageY != null ? event.pageY : event.clientY + (win.scrollY || 0))),
      h: Math.max(doc.documentElement?.scrollHeight || 0, doc.body?.scrollHeight || 0),
      vw: win.innerWidth,
      tg: describeElement(event.target),
      hr: linkOf(event.target),
    });
  };

  const recordSearch = (input) => {
    const view = state.view;
    const term = String(input.value || "").trim();
    if (!view || term.length < 2) return;
    const label = (input.getAttribute("aria-label") || input.getAttribute("placeholder") || input.getAttribute("name") || "Arama").slice(0, 120);
    const key = `${view.id}|${label}`;
    if (state.lastSearch.get(key) === term.toLocaleLowerCase("tr-TR")) return;
    state.lastSearch.set(key, term.toLocaleLowerCase("tr-TR"));
    push({ t: "search", pv: view.id, p: view.path, q: term.slice(0, 120), tg: label });
  };

  const onInput = (event) => {
    const input = event.target;
    if (!input?.matches?.(SEARCH_INPUT)) return;
    clearTimeout(state.searchTimers.get(input));
    state.searchTimers.set(input, setTimeout(() => recordSearch(input), SEARCH_SETTLE_MS));
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && event.target?.matches?.(SEARCH_INPUT)) {
      clearTimeout(state.searchTimers.get(event.target));
      recordSearch(event.target);
    }
  };

  const onError = (event) => {
    const view = state.view;
    if (!view || view.errors >= MAX_ERRORS_PER_VIEW) return;
    const reason = event?.reason;
    const message = event?.message || reason?.message || (typeof reason === "string" ? reason : "") || "Bilinmeyen hata";
    view.errors += 1;
    push({ t: "error", p: view.path, m: String(message).slice(0, 200) });
  };

  const reportVitals = () => {
    import("web-vitals").then((vitals) => {
      const report = (metric) => {
        if (!state.view) return;
        push({ t: "vital", p: state.view.path, n: metric.name, val: metric.value });
      };
      ["getLCP", "getFCP", "getCLS", "getFID", "getTTFB"].forEach((fn) => {
        try { vitals[fn]?.(report); } catch { /* desteklenmiyor */ }
      });
    }).catch(() => {});
  };

  /* --- genel arayüz --- */

  function start() {
    if (state.started) return;
    state.started = true;
    win.addEventListener("scroll", onScroll, { passive: true });
    doc.addEventListener("visibilitychange", onVisibility);
    win.addEventListener("pagehide", () => { sendEngage(); flush({ beacon: true }); });
    doc.addEventListener("click", onClick, { capture: true, passive: true });
    doc.addEventListener("input", onInput, true);
    doc.addEventListener("keydown", onKeyDown, true);
    ["keydown", "pointermove", "touchstart", "wheel"].forEach((type) => doc.addEventListener(type, onActivity, { capture: true, passive: true }));
    win.addEventListener("error", onError);
    win.addEventListener("unhandledrejection", onError);
    state.timer = setInterval(() => flush(), FLUSH_MS);
    reportVitals();
  }

  /** Sayfa değişiminde çağrılır. İzlenmeyen yolda önceki sayfayı kapatır. */
  function pageview(path, title = "") {
    if (state.view) {
      sendEngage();
      state.view = null;
    }
    const pathname = String(path || "/").split("?")[0];
    if (!isTrackablePath(pathname)) {
      flush();
      return;
    }
    touchSession();
    const entry = state.session.pages === 0;
    state.session.pages += 1;
    storage.set(SESSION_KEY, JSON.stringify(state.session));
    markActive();
    state.view = {
      id: randomId(12),
      path: String(path || "/").slice(0, 400),
      visibleSince: doc.visibilityState === "hidden" ? null : now(),
      activeMs: 0,
      sentMs: 0,
      maxScroll: 0,
      sentScroll: 0,
      clicks: 0,
      errors: 0,
    };
    state.firstLoad = false;
    push({ t: "pageview", pv: state.view.id, p: state.view.path, ti: String(title || "").slice(0, 200), en: entry ? 1 : 0 });
    // İlk çizimden sonra kaydırma derinliği (kısa sayfalar hiç kaydırılmadan %100 olabilir).
    setTimeout(onScroll, 1500);
  }

  return { start, pageview, flush, _state: state, _handlers: { onClick, onInput, onKeyDown, onVisibility, onError, onActivity, sendEngage } };
}

/** Tarayıcıda izlemenin hiç başlamaması gereken durumlar. */
export const trackingDisabled = (win = window) => {
  try {
    if (win.top !== win.self) return true; // ısı haritası önizlemesi ve gömülü görünümler
  } catch {
    return true;
  }
  if (win.navigator?.webdriver) return true;
  if (isOptedOut()) return true;
  return process.env.NODE_ENV === "test";
};
